<template>
  <transition name="fade">
    <div v-if="visible" class="fixed inset-0 z-[100] flex items-center justify-center bg-black/80"
         @click.self="handleBackdropClick">
      <div
          :class="['relative w-full max-w-lg mx-4 p-6 md:p-8 border-4 transition-all', isEasy ? 'bg-amber-50 border-amber-800/50 sepia-shadow' : 'card-dark-rich border-amber-600/60 shadow-2xl shadow-black/40']"
          style="border-radius: 8px;">
        <!-- Close button (only after ad watched) -->
        <button
            v-if="canSkip"
            @click="handleSkip"
            :class="['absolute top-2 right-2 w-8 h-8 flex items-center justify-center border-2 transition-colors', isEasy ? 'text-amber-800 border-amber-700/50 hover:bg-amber-100' : 'text-amber-300 border-amber-700/40 hover:bg-stone-700']"
            style="border-radius: 4px;"
        >
          <span class="text-lg">&times;</span>
        </button>

        <!-- Ad Content Area -->
        <div class="space-y-6">
          <!-- Header -->
          <div class="text-center">
            <p :class="['text-xs font-serif-body font-bold uppercase tracking-widest', isEasy ? 'text-amber-900/60' : 'text-amber-400/60']">
              Sponsored Message
            </p>
          </div>

          <!-- Placeholder Ad Content -->
          <div
              :class="['aspect-video flex items-center justify-center border-2 border-dashed', isEasy ? 'bg-amber-100/50 border-amber-700/30' : 'bg-stone-800/50 border-slate-600/50']"
              style="border-radius: 6px;">
            <div class="text-center p-4">
              <div :class="['text-4xl mb-2', isEasy ? 'text-amber-700' : 'text-amber-500']">
                <span class="inline-block animate-pulse">AD</span>
              </div>
              <p :class="['text-sm font-serif-body', isEasy ? 'text-amber-800/70' : 'text-amber-300/70']">
                Advertisement Placeholder
              </p>
              <p :class="['text-xs font-serif-body mt-2', isEasy ? 'text-amber-800/50' : 'text-amber-300/50']">
                Real ads will appear here when configured
              </p>
            </div>
          </div>

          <!-- Skip/Continue Button -->
          <div class="text-center">
            <button
                @click="handleSkip"
                :disabled="!canSkip"
                :class="[
                'px-6 py-3 font-serif-body font-bold border-2 transition-all',
                canSkip
                  ? (isEasy ? 'bg-amber-700 hover:bg-amber-600 border-amber-900 text-amber-50' : 'bg-amber-700 hover:bg-amber-500 border-amber-900 text-white')
                  : (isEasy ? 'bg-amber-200 border-amber-400 text-amber-600 cursor-not-allowed' : 'bg-stone-700 border-stone-600 text-stone-400 cursor-not-allowed')
              ]"
                style="border-radius: 6px;"
            >
              <span v-if="!canSkip">Skip in {{ countdown }}...</span>
              <span v-else>Continue</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import {onUnmounted, ref, watch} from 'vue'
import {adService} from '../services/adService'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  isEasy: {
    type: Boolean,
    default: false
  },
  adConfig: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['complete', 'skip', 'close'])

const countdown = ref(3)
const canSkip = ref(false)
const adWatched = ref(false)
let countdownTimer = null

const startCountdown = () => {
  countdown.value = Math.ceil(adService.SKIP_DELAY / 1000)
  canSkip.value = false
  adWatched.value = false

  countdownTimer = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      clearInterval(countdownTimer)
      canSkip.value = true
      adWatched.value = true
    }
  }, 1000)
}

const stopCountdown = () => {
  if (countdownTimer) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
}

watch(() => props.visible, (newVal) => {
  if (newVal) {
    startCountdown()
  } else {
    stopCountdown()
  }
})

const handleSkip = () => {
  if (!canSkip.value) return
  stopCountdown()

  if (props.adConfig?.onSkip) {
    props.adConfig.onSkip()
  }
  emit('skip')
}

const handleBackdropClick = () => {
  // Only allow closing via backdrop after ad is watched
  if (adWatched.value) {
    handleSkip()
  }
}

onUnmounted(() => {
  stopCountdown()
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.sepia-shadow {
  box-shadow: 4px 4px 0px rgba(120, 80, 40, 0.2);
}

.card-dark-rich {
  background: radial-gradient(circle at top left, #2d2a28, #1a1817);
}
</style>
